import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  saveCategory,
  deleteCategory,
} from "../../Controllers/categoryController";
import { fetchActivities } from "../../Controllers/activityController";
import { useError } from "../../Context/errorContext";

const AdminCategories = ({
  setCategories,
  setUnitFilter,
  setActivityFilter,
  categories,
  units,
  unitFilter,
  activityFilter,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [isEditingCat, setIsEditingCat] = useState(false);
  const [unitInput, setUnitInput] = useState("");
  const [activityInput, setActivityInput] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const saveRefCat = useRef(null);
  const { setAlert } = useError();

  function validateCategory() {
    if (selectedCategory.unit_id === null) {
      setAlert({
        severity: "warning",
        message: `Unit field is required`,
      });
      return;
    }
    if (selectedCategory.activity_id === null) {
      setAlert({
        severity: "warning",
        message: `Activity field is required`,
      });
      return;
    }
    if (selectedCategory.category_name === null) {
      setAlert({
        severity: "warning",
        message: `Category name field is required`,
      });
      return;
    }
    if (selectedCategory.code_position === null) {
      setAlert({
        severity: "warning",
        message: `Code Position field is required`,
      });
      return;
    }
    if (selectedCategory.code_length === null) {
      setAlert({
        severity: "warning",
        message: `Code length field is required`,
      });
      return;
    }
    if (selectedCategory.required === null) {
      setAlert({
        severity: "warning",
        message: `Required field is required`,
      });
      return;
    }
    return true;
  }

  const handleSaveCat = async () => {
    if (!validateCategory()) return;
    saveCategory(selectedCategory, setAlert, setIsEditingCat, setCategories);
  };

  const handleDeleteCat = async (id) => {
    deleteCategory(id, setAlert, setCategories);
  };

  const handleCancelCat = () => {
    if (
      !(
        selectedCategory.unit_id === null &&
        selectedCategory.activity_id === null &&
        selectedCategory.category_name === null &&
        selectedCategory.code_position === null &&
        selectedCategory.code_length === null &&
        selectedCategory.required === null &&
        selectedCategory.parent_category_id === null
      )
    ) {
      const confirm = window.confirm("Cancel category changes?");
      if (!confirm) return;
    }
    setIsEditingCat(false);
  };

  const handleClearAll = () => {
    setUnitFilter(null);
    setActivityFilter(null);
    setUnitInput("");
    setActivityInput("");
    setResetKey((k) => k + 1); // forces a clean remount if needed
  };

  const availableLengths = useMemo(() => {
    if (
      !selectedCategory?.unit_id ||
      !selectedCategory?.activity_id ||
      !selectedCategory?.code_position
    )
      return [];
    const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

    const cats = categories.filter(
      (c) =>
        c.unit_id === selectedCategory.unit_id &&
        c.activity_id === selectedCategory.activity_id &&
        c.code_position > selectedCategory.code_position,
    );
    if (cats.length === 0) return range(8 - selectedCategory.code_position);

    const nextCat = cats.reduce(
      (min, c) => (!min || c.code_position < min.code_position ? c : min),
      null,
    );
    return range(nextCat.code_position - selectedCategory.code_position);
  }, [categories, selectedCategory]);

  const availablePositions = useMemo(() => {
    if (!(selectedCategory?.unit_id || selectedCategory?.activity_id))
      return [];
    const positions = [4, 5, 6, 7];
    const cats = categories.filter(
      (c) =>
        c.unit_id === selectedCategory.unit_id &&
        c.activity_id === selectedCategory.activity_id,
    );
    for (const category of cats) {
      const i = positions.indexOf(category.code_position);
      if (i !== -1 && category.category_id !== selectedCategory.category_id)
        positions.splice(i, category.code_length);
    }

    return positions;
  }, [categories, selectedCategory]);

  // currently selected unit object (or null)
  const selectedUnit = useMemo(
    () => (units || []).find((u) => u.unit_id === unitFilter) || null,
    [units, unitFilter],
  );

  const filteredCategories = useMemo(
    () =>
      categories.filter(
        (c) =>
          (!unitFilter || c.unit_id === unitFilter) &&
          (!activityFilter || c.activity_id === activityFilter),
      ) || [],
    [unitFilter, activityFilter, categories],
  );

  // activity options depend on selected unit; otherwise show all activities (deduped)
  const filterActivityOptions = useMemo(() => {
    if (selectedUnit) return selectedUnit.activities || [];

    return activities;
  }, [selectedUnit, activities]);

  const activityOptions = useMemo(() => {
    if (selectedCategory?.unit_id) {
      const unit = units.find((u) => u.unit_id === selectedCategory.unit_id);
      return unit.activities || [];
    }

    return [];
  }, [selectedCategory, units]);

  useEffect(() => {
    if (!categories) return;
    let res = [...categories];
    res = res.filter(
      (c) =>
        c.unit_id === selectedCategory?.unit_id &&
        c.activity_id === selectedCategory?.activity_id &&
        c.code_position + c.code_length ===
          parseInt(selectedCategory?.code_position),
    );
    setCategoryOptions(res);
  }, [selectedCategory, categories]);

  useEffect(() => {
    fetchActivities(setActivities);
  }, []);

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {/* Unit filter */}
            <Autocomplete
              key={`unit-${resetKey}`} // optional: ensure clean slate
              options={units || []}
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
              getOptionLabel={(o) => o.unit_name}
              isOptionEqualToValue={(o, v) => o.unit_id === v.unit_id}
              value={selectedUnit}
              onChange={(e, newValue) => {
                setUnitFilter(newValue ? newValue.unit_id : null);
                setActivityFilter(null);
                setActivityInput(""); // clear the other box too
                if (!newValue) setUnitInput(""); // clear text when cleared
              }}
              inputValue={unitInput}
              onInputChange={(e, newInput, reason) => {
                setUnitInput(newInput ?? "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by unit"
                  placeholder="All units"
                  // stop browser autofill from ghosting text
                  autoComplete="off"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                  }}
                />
              )}
            />

            {/* Activity filter */}
            <Autocomplete
              key={`activity-${resetKey}`} // optional: remount when we clear all
              options={filterActivityOptions || []}
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
              getOptionLabel={(o) => o.activity_name}
              isOptionEqualToValue={(o, v) => o.activity_id === v.activity_id}
              value={
                (filterActivityOptions || []).find(
                  (a) => a.activity_id === activityFilter,
                ) || null
              }
              onChange={(e, newValue) => {
                setActivityFilter(newValue ? newValue.activity_id : null);
                if (!newValue) setActivityInput("");
              }}
              inputValue={activityInput}
              onInputChange={(e, newInput) => setActivityInput(newInput ?? "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by activity"
                  placeholder={
                    selectedUnit ? "Activities in unit" : "All activities"
                  }
                  autoComplete="off"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                  }}
                />
              )}
            />

            <Button variant="contained" onClick={handleClearAll}>
              Clear
            </Button>
          </Box>
        </Paper>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="h6">Categories</Typography>
          <Typography variant="body2" color="text.secondary">
            Headers for csv: [category_name; parent_subactivity_code;
            subactivity_code; subactivity_name;]
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            setSelectedCategory({
              unit_id: null,
              activity_id: null,
              category_name: null,
              code_position: null,
              code_length: null,
              required: null,
              parent_category_id: null,
            });
            setIsEditingCat(true);
          }}
        >
          Add Category
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Categories table */}
      <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Activity</TableCell>
              <TableCell>Code Position</TableCell>
              <TableCell>Code Length</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Parent Categoty</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.map((cat) => {
              return (
                <TableRow key={cat.category_id} hover>
                  <TableCell>{cat.category_name}</TableCell>
                  <TableCell>{cat.Unit?.unit_name}</TableCell>
                  <TableCell>{cat.Activity?.activity_name}</TableCell>
                  <TableCell>{cat.code_position}</TableCell>
                  <TableCell>{cat.code_length}</TableCell>
                  <TableCell>{String(cat.required)}</TableCell>
                  <TableCell>{cat.Parent?.category_name || "—"}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "inline-flex", gap: 1 }}>
                      <Button
                        onClick={() => {
                          setSelectedCategory(cat);
                          setIsEditingCat(true);
                        }}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button
                        color="error"
                        onClick={() => handleDeleteCat(cat.category_id)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Category dialog */}
      <Dialog
        open={isEditingCat}
        onClose={() => {
          setIsEditingCat(false);
        }}
        fullWidth
      >
        <DialogTitle>
          {selectedCategory?.category_id ? "Edit Category" : "Add Category"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            options={units}
            getOptionLabel={(option) => option.unit_name}
            isOptionEqualToValue={(option, value) =>
              option.unit_id === value.unit_id
            }
            value={
              units.find((u) => u.unit_id === selectedCategory?.unit_id) || null
            }
            onChange={(e, newValue) =>
              setSelectedCategory({
                ...selectedCategory,
                unit_id: newValue ? newValue.unit_id : null,
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Unit" fullWidth margin="normal" />
            )}
          />

          <Autocomplete
            options={activityOptions}
            getOptionLabel={(option) => option.activity_name}
            isOptionEqualToValue={(option, value) =>
              option.activity_id === value.activity_id
            }
            value={
              activityOptions.find(
                (a) => a.activity_id === selectedCategory?.activity_id,
              ) || null
            }
            onChange={(e, newValue) =>
              setSelectedCategory({
                ...selectedCategory,
                activity_id: newValue ? newValue.activity_id : null,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Activity"
                fullWidth
                margin="normal"
              />
            )}
          />

          <TextField
            fullWidth
            label="Category Name"
            value={selectedCategory?.category_name || ""}
            onChange={(e) =>
              setSelectedCategory({
                ...selectedCategory,
                category_name: e.target.value,
              })
            }
            margin="normal"
          />

          <Autocomplete
            options={availablePositions}
            getOptionLabel={(option) => String(option)}
            value={
              availablePositions.find(
                (a) => a === selectedCategory?.code_position,
              ) || null
            }
            onChange={(e, newValue) =>
              setSelectedCategory({
                ...selectedCategory,
                code_position: newValue,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Code Position"
                fullWidth
                margin="normal"
              />
            )}
          />

          <Autocomplete
            options={availableLengths}
            getOptionLabel={(option) => String(option)}
            value={
              availableLengths.find(
                (a) => a === selectedCategory?.code_length,
              ) || null
            }
            onChange={(e, newValue) =>
              setSelectedCategory({
                ...selectedCategory,
                code_length: newValue,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Code Length"
                fullWidth
                margin="normal"
              />
            )}
          />

          <TextField
            select
            fullWidth
            margin="normal"
            label="Required"
            value={selectedCategory?.required}
            onChange={(e) =>
              setSelectedCategory({
                ...selectedCategory,
                required: e.target.value,
              })
            }
          >
            <MenuItem value={true}>True</MenuItem>
            <MenuItem value={false}>False</MenuItem>
          </TextField>

          <Autocomplete
            options={categoryOptions}
            getOptionLabel={(option) => option.category_name}
            isOptionEqualToValue={(option, value) =>
              option.category_id === value.category_id
            }
            value={
              categoryOptions.find(
                (a) => a.category_id === selectedCategory?.parent_category_id,
              ) || null
            }
            onChange={(e, newValue) =>
              setSelectedCategory({
                ...selectedCategory,
                parent_category_id: newValue ? newValue.category_id : null,
              })
            }
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                setTimeout(() => {
                  saveRefCat.current?.focus();
                }, 0);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parent Category"
                fullWidth
                margin="normal"
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCancelCat}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveCat} ref={saveRefCat}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminCategories;
